import { makeAutoObservable } from "mobx";
import { groupBy, sortBy, sumBy, uniqBy } from "lodash";
import { DecisionType } from "@/common/decisions";
import { OptionFieldModel } from "@/common/commonForms";
import { DecisionContext, GrantLetterCandidate } from "@/dbot/common/commonDomain";
import { TMessage } from "@/i18n/tt";

export type GrantLetterProgram = {
  planId: string;
  holderIds: string[];
};

export type OptionGrantLetter = {
  type: DecisionType.optionGrantLetter;
  programs: GrantLetterProgram[];
};

export type ProgramChoice = {
  planId: string;
  programName: string;
};

export type ProgramHolder = {
  holderId: string;
  holderName: string;
  shareholderId?: number;
  numberOfOptions: number;
};

export const programChoices = (candidates: GrantLetterCandidate[]): ProgramChoice[] =>
  sortBy(
    uniqBy(candidates, (candidate) => candidate.planId).map(({ planId, programName }) => ({
      planId,
      programName,
    })),
    (choice) => choice.programName,
  );

export const programHolders = (
  candidates: GrantLetterCandidate[],
  planId: string,
): ProgramHolder[] =>
  Object.values(
    groupBy(
      candidates.filter((candidate) => candidate.planId === planId),
      (candidate) => candidate.holderId,
    ),
  ).map((grants) => ({
    holderId: grants[0].holderId,
    holderName: grants[0].holderName,
    shareholderId: grants[0].shareholderId,
    numberOfOptions: sumBy(grants, (grant) => grant.numberOfOptions),
  }));

export const canGetLetter = (holder: ProgramHolder): boolean => holder.shareholderId !== undefined;

export const programHolderLabel = (holder: ProgramHolder): TMessage => ({
  key: "decisions.optionGrantLetter.holder",
  params: { holder: holder.holderName, count: holder.numberOfOptions },
});

const candidatesIn = (context: DecisionContext<OptionGrantLetter>): GrantLetterCandidate[] =>
  context.contextData[context.orgNo]?.optionGrants ?? [];

export class GrantLetterProgramModel {
  readonly planId: string;
  readonly holders: OptionFieldModel<ProgramHolder>;
  private readonly context: DecisionContext<OptionGrantLetter>;

  constructor(context: DecisionContext<OptionGrantLetter>, planId: string, holderIds: string[]) {
    this.context = context;
    this.planId = planId;
    this.holders = new OptionFieldModel<ProgramHolder>({
      selectedIds: holderIds,
      options: () => programHolders(candidatesIn(this.context), this.planId),
      idFn: (holder) => holder.holderId,
      labelFn: programHolderLabel,
      disabledFn: (holder) => !canGetLetter(holder),
      tooltipFn: (holder) =>
        canGetLetter(holder) ? undefined : { key: "decisions.optionGrantLetter.no-shareholder" },
    });
    makeAutoObservable<GrantLetterProgramModel, "context">(
      this,
      { planId: false, context: false },
      { autoBind: true },
    );
  }

  get programName(): string {
    return (
      candidatesIn(this.context).find((candidate) => candidate.planId === this.planId)
        ?.programName ?? ""
    );
  }

  toData(): GrantLetterProgram {
    return { planId: this.planId, holderIds: [...this.holders.selectedIds] };
  }
}

export class OptionGrantLetterForm {
  readonly type = DecisionType.optionGrantLetter;
  readonly context: DecisionContext<OptionGrantLetter>;
  programs: GrantLetterProgramModel[];
  error?: TMessage = undefined;

  constructor(context: DecisionContext<OptionGrantLetter>, data?: OptionGrantLetter) {
    this.context = context;
    this.programs = (data?.programs ?? []).map(
      (program) => new GrantLetterProgramModel(context, program.planId, program.holderIds),
    );
    makeAutoObservable(this, { type: false, context: false }, { autoBind: true });
  }

  get candidates(): GrantLetterCandidate[] {
    return candidatesIn(this.context);
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get availablePrograms(): ProgramChoice[] {
    return programChoices(this.candidates).filter(
      (choice) => !this.programs.some((program) => program.planId === choice.planId),
    );
  }

  addProgram(planId: string) {
    const holderIds = programHolders(this.candidates, planId)
      .filter(canGetLetter)
      .map((holder) => holder.holderId);
    this.programs.push(new GrantLetterProgramModel(this.context, planId, holderIds));
    this.error = undefined;
  }

  removeProgram(planId: string) {
    this.programs = this.programs.filter((program) => program.planId !== planId);
  }

  get isValid(): boolean {
    return this.programs.some((program) => !program.holders.isNoneSelected);
  }

  validate(): boolean {
    this.error = this.isValid
      ? undefined
      : { key: "decisions.optionGrantLetter.no-holders-selected" };
    return this.isValid;
  }

  toData(): OptionGrantLetter {
    return {
      type: DecisionType.optionGrantLetter,
      programs: this.programs.map((program) => program.toData()),
    };
  }
}
